import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";

// Texas ZIPs start with 7XXXX or 88XXX (El Paso area).
const TX_ZIP = /^(7\d{4}|88\d{3})$/;

export default function GetStartedZipBanner() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [zip, setZip] = useState("");
  const [error, setError] = useState("");

  const onChange = (v) => {
    const cleaned = v.replace(/\D/g, "").slice(0, 5);
    setZip(cleaned);
    if (error) setError("");
  };

  const submit = (e) => {
    if (e) e.preventDefault();
    if (zip.length !== 5) {
      setError(t("home.getStartedErrShort"));
      return;
    }
    if (!TX_ZIP.test(zip)) {
      setError(t("home.getStartedErrTx"));
      return;
    }
    localStorage.setItem("velcor_zip", zip);
    navigate(`/address?zip=${zip}`);
  };

  return (
    <section
      className="relative w-full my-14 md:my-20"
      data-testid="get-started-banner"
    >
      <div
        className="relative overflow-hidden px-7 sm:px-10 md:px-16 py-9 md:py-12"
        style={{
          background: "linear-gradient(135deg, #0b1226 0%, #0f172a 60%, #15321b 100%)",
        }}
      >
        {/* subtle green glow */}
        <div
          aria-hidden
          className="absolute -right-10 top-1/2 -translate-y-1/2 w-[420px] h-[420px] rounded-full pointer-events-none"
          style={{
            background:
              "radial-gradient(circle, rgba(126,217,87,0.15), rgba(47,122,58,0.06) 50%, transparent 75%)",
          }}
        />

        <div className="relative max-w-[1180px] mx-auto flex flex-col md:flex-row items-center justify-between gap-6 md:gap-10">
          {/* Title */}
          <h2 className="display text-[28px] sm:text-[32px] md:text-[40px] text-white leading-tight tracking-tight text-center md:text-left">
            {t("home.getStartedTitle")}{" "}
            <span style={{ color: "#7ed957" }}>{t("home.getStartedAccent")}</span>
          </h2>

          {/* Minimal input */}
          <form onSubmit={submit} className="w-full md:w-auto" data-testid="get-started-form">
            <div className="flex items-center bg-white rounded-full p-1 w-full md:w-[360px] shadow-[0_10px_30px_rgba(0,0,0,0.25)]">
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={5}
                placeholder={t("home.getStartedPlaceholder")}
                value={zip}
                onChange={(e) => onChange(e.target.value)}
                className="flex-1 min-w-0 bg-transparent outline-none px-5 h-12 text-[15px] font-extrabold text-velcor-ink placeholder:text-slate-400 placeholder:font-bold tracking-wide"
                data-testid="get-started-zip-input"
                aria-label="ZIP"
              />
              <button
                type="submit"
                className="grid place-items-center rounded-full bg-velcor-green text-white w-12 h-12 hover:bg-velcor-green/90 active:scale-95 transition shrink-0"
                data-testid="get-started-submit"
                aria-label={t("home.getStartedCta")}
              >
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
            {error && (
              <p
                className="mt-2 text-[12px] font-extrabold text-rose-300 text-center md:text-left pl-2"
                data-testid="get-started-error"
              >
                {error}
              </p>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}
